import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Dialog, DialogActions, DialogDescription, DialogTitle } from "src/components/base/dialog";
import { Button } from "../base/button";
import { ApiInternal } from "src/api/api_internal";
import { SimpleTag } from "src/api/generated/frontend_internal_v1";

/**
 * The properties for {@link DeleteTagDialog}
 */
export type DeleteTagDialogProps = {
    /** Callback for closing the popup */
    onClose: () => void;

    /** Callback for successful deletion */
    onDeletion: () => void;

    /** The tag */
    tag: SimpleTag;
};

/**
 * A dialog to delete a tag
 */
export default function DeleteTagDialog(props: DeleteTagDialogProps) {
    const [t] = useTranslation("dialog-delete-tag");
    const [tg] = useTranslation();

    const [errors, setErrors] = React.useState<Array<string>>([]);

    /**
     * Delete a tag
     */
    const deleteTag = async () => {
        const res = await ApiInternal.tags.delete(props.tag.uuid);
        if (res.result === "Err") {
            const errors = [];
            if (res.error.devices) {
                errors.push(t("error.devices-with-tag", { count: res.error.devices }));
            }
            if (res.error.access_rules) {
                errors.push(t("error.access-rules-with-tag", { count: res.error.access_rules }));
            }
            if (res.error.preauthkeys) {
                errors.push(t("error.preauthkeys-with-tag", { count: res.error.preauthkeys }));
            }

            setErrors(errors);

            return;
        }

        toast.success(t("toast.tag-deleted-successfully"));
        props.onDeletion();
    };

    return (
        <Dialog open={true} onClose={props.onClose}>
            <DialogTitle>{t("heading.delete-tag", { tag: props.tag.name })}</DialogTitle>
            <DialogDescription>{t("description.delete-tag")}</DialogDescription>
            {errors.length > 0 && (
                <div className={"mt-3 flex flex-col gap-1 text-sm text-red-500 dark:text-red-700"}>
                    {errors.map((err, idx) => (
                        <span key={idx}>{err}</span>
                    ))}
                </div>
            )}
            <DialogActions>
                <Button onClick={props.onClose}>{tg("button.cancel")}</Button>
                <Button color={"red"} onClick={async () => await deleteTag()}>
                    {t("button.delete-tag")}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
